import * as Sentry from '@sentry/react';
import { Box, Button, Stack, Text } from '@mantine/core';

/**
 * Empty DSN → no-op SDK, so `pnpm build` and E2E never need Sentry to exist (§8.1).
 * Events are tunnelled through the API so ad blockers cannot silently drop them.
 */
export function initWebSentry(): boolean {
  const dsn = import.meta.env.VITE_SENTRY_DSN ?? '';
  if (!dsn) return false;

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? 'development',
    release: import.meta.env.VITE_GIT_SHA ?? 'unknown',
    tunnel: `${import.meta.env.VITE_API_URL ?? ''}/monitoring-tunnel`,
    tracesSampleRate: Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? '0'),
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    // Replays only on errors: a CRM screen full of client data is not something to record.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.1,
    sendDefaultPii: false,
    ignoreErrors: ['AbortError', 'NetworkError', 'Failed to fetch', 'Load failed'],
  });
  return true;
}

function Fallback({ resetError }: { resetError: () => void }): React.JSX.Element {
  return (
    <Box
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg-deep)',
        padding: 24,
      }}
      data-testid="error-boundary"
    >
      <Stack
        gap="md"
        style={{
          maxWidth: 460,
          background: 'var(--card)',
          border: '1px solid var(--line2)',
          borderRadius: 12,
          padding: '28px 26px',
        }}
      >
        <Text
          style={{
            fontSize: 'var(--fs-title)',
            fontWeight: 600,
            lineHeight: 'var(--lh-snug)',
            color: 'var(--t0)',
          }}
        >
          Щось пішло не так
        </Text>
        <Text style={{ fontSize: 'var(--fs-base)', lineHeight: 'var(--lh-base)', color: 'var(--t7)' }}>
          Помилку записано. Спробуйте повторити дію — якщо вона повториться, зверніться до
          адміністратора з часом і адресою сторінки.
        </Text>
        <Button onClick={resetError} data-testid="error-retry">
          Спробувати ще раз
        </Button>
      </Stack>
    </Box>
  );
}

export function ErrorBoundary({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <Sentry.ErrorBoundary
      fallback={(props: { resetError: () => void }) => (
        <Fallback resetError={() => { props.resetError(); }} />
      )}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}

export { Sentry };
